import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AdjustmentReason } from '../../models/adjustment-reasons.model';
import { Pagination } from '../../models/response.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-adjustment-reasons',
  templateUrl: './adjustment-reasons.component.html',
  styleUrl: './adjustment-reasons.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdjustmentReasonsComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<AdjustmentReason>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefService = inject(ColumnPreferencesService);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);
  private dialog = inject(MatDialog);

  reasonsList: string[] = [];
  descriptionsList: string[] = [];
  typesList: string[] = [];
  adjustableList: string[] = [];
  activeList: string[] = ['Yes', 'No'];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;

  adjustmentReasons: AdjustmentReason[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-AdjustmentReasons-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  columnNames: Record<string, string> = {};
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getAdjustmentReasons(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.adjustmentReasons as AdjustmentReason[]).map((item) => ({
          adjReasons: item.adjReasons,
          description: item.description,
          type: item.type,
          condition: item.condition,
          adjustable: item.adjustable,
          active:
            item.active === 'Y' || item.active === '1'
              ? this.translationService.translate('activeText')
              : this.translationService.translate('inactiveText')
        }))
      }))
    );

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getAdjustmentReasons({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.adjustmentReasons = response.data.adjustmentReasons || [];
        this.pagination = response.data.pagination;
        return { items: this.adjustmentReasons, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      adjReason: [''],
      description: [''],
      type: [''],
      adjustable: [''],
      active: ['Yes']
    });
    this.filterFormBackend = this.fb.group({
      adjReason: [''],
      description: [''],
      type: [''],
      adjustable: [''],
      active: ['Yes']
    });
  }

  ngOnInit(): void {
    this.productDataService.getAdjustmentReasonsFilters().subscribe((response: any) => {
      this.reasonsList = response.data.reasons || [];
      this.descriptionsList = response.data.descriptions || [];
      this.typesList = response.data.types || [];
      this.adjustableList = response.data.adjustable || [];
      this.cdr.markForCheck();
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.canonicalColumns = {
        adjReasons: { cell: (row) => row.adjReasons, header: this.translationService.translate('adjustmentReasonText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
        type: { cell: (row) => row.type, header: this.translationService.translate('typeColumnText') },
        condition: { cell: (row) => row.condition, header: this.translationService.translate('conditionText') },
        adjustable: { cell: (row) => row.adjustable, header: this.translationService.translate('adjustableText') },
        active: { cell: 'activeCell', header: this.translationService.translate('statusText') }
      };

      this.columnNames = {
        adjReasons: this.translationService.translate('adjustmentReasonText'),
        description: this.translationService.translate('descriptionText'),
        type: this.translationService.translate('typeColumnText'),
        condition: this.translationService.translate('conditionText'),
        adjustable: this.translationService.translate('adjustableText'),
        active: this.translationService.translate('statusText')
      };

      // initialize columns with user preferences (localStorage)
      const init = this.columnPrefsHelper.initialize('adjustment-reasons', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'adjustment-reasons',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  clearFilters(): void {
    this.filterForm.reset({
      reasons: '',
      descriptions: '',
      types: '',
      adjustable: '',
      active: 'Yes'
    });
  }

  private generateFilterForm(): void {
    const filterReasons = this.filterForm.get('adjReason').value;
    const filterDescriptions = this.filterForm.get('description').value;
    const filterTypes = this.filterForm.get('type').value;
    const filterAdjustable = this.filterForm.get('adjustable').value;
    const filterActive = this.filterForm.get('active').value;
    this.filterFormBackend.patchValue({
      adjReason: filterReasons,
      description: filterDescriptions,
      type: filterTypes,
      adjustable: filterAdjustable,
      active: filterActive === 'Yes' ? 1 : 0
    });
  }
}
